import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ADMIN,
  CLIENT,
  CLIENT_VENDOR,
  PROJECT_COORDINATOR,
} from "../../constants/roles";
import { useDispatch } from "react-redux";
import { setUser } from "../../store/authSlice";
import { AuthService } from "../../services/AuthService";
import { IoCallOutline } from "react-icons/io5";
import { MdMailOutline } from "react-icons/md";
import { Logo, welogo } from "../../Assets/common";
import DisclaimerModal from "../../Modal/DisclaimerModal";

export default function OtpVerification() {
  const location = useLocation();
  const dispatch = useDispatch();
  const role = location.state?.role;
  const email = location?.state?.email;

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(180);
  const [isResending, setIsResending] = useState(false);
  const [isDisclaimerOpen, setDisclaimerOpen] = useState(false);
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const joinedOtp = otp.join("");

    if (otp.includes("") || joinedOtp.length < 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    try {
      const response =
        role === CLIENT
          ? await AuthService.verifyClientOtp({ email, otp: joinedOtp })
          : await AuthService.verifyOtp({ email, otp: joinedOtp });
      console.log("otp response",response);
      if (response) {
        toast.success("OTP Verified Successfully!");
        setTimeout(() => {
          dispatch(setUser({ user: response, accessToken: response?.token }));

          if (response?.role === ADMIN) {
            sessionStorage.setItem("header", "Dashboard");
            navigate("/admin/dashboard");
          } else if (response?.role === PROJECT_COORDINATOR) {
            sessionStorage.setItem("header", "Dashboard");
            navigate("/project-coordinator/dashboard");
          } else if (
            response?.role === CLIENT ||
            response?.role === CLIENT_VENDOR
          ) {
            sessionStorage.setItem("header", "Dashboard");
            navigate("/client/dashboard");
          }
        }, 1000);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      await AuthService.resendOtp({ email, role });
      toast.success("OTP resent successfully!");
      setTimer(60);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 px-4 relative">
      {/* Header */}
      <div className="flex items-center gap-10 py-3 pl-3 md:pl-8">
        <img src={Logo} alt="logo" className="w-36 h-12 xl:w-60 xl:h-20" />
      </div>

      {/* Main Section */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
        {/* Left Text */}
        <div className="w-full md:w-[50%] mt-6">
          <h1 className="text-3xl md:text-5xl font-semibold text-purple-800 mb-2">
            Welcome to <span className="text-purple-600">MRR PMS</span>
          </h1>

          <p className="text-lg md:text-2xl text-purple-700 font-medium mb-1">
            Your personal scientific assistant
          </p>

          <p className="text-sm md:text-lg italic text-gray-600">
            by{" "}
            <a
              href="https://myresearchroom.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 text-transparent bg-clip-text hover:from-purple-600 hover:to-indigo-600 transition"
            >
              MyResearchRoom
            </a>
          </p>
        </div>

        {/* OTP Card */}
        <div className="flex flex-col bg-white p-6 md:p-8 rounded-md shadow-2xl w-full max-w-md border-t-4 border-purple-800">
          <h2 className="text-lg md:text-xl xl:text-2xl font-semibold text-center text-gray-800 mb-4">
            ENTER OTP
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="mt-6 sm:mt-10 flex justify-between gap-1 sm:gap-2 mb-4 sm:mb-6">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  className="w-9 h-9 sm:w-12 sm:h-12 text-center border border-gray-300 rounded-md text-base md:text-lg outline-none focus:ring-2 focus:ring-blue-400"
                />
              ))}
            </div>

            <button
              type="submit"
              className="mt-6 sm:mt-10 w-full py-3 rounded-md text-white font-semibold transition bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
            >
              Verify OTP
            </button>

            <div className="mt-2 text-center text-sm md:text-base text-gray-600 mb-6">
              {timer > 0 ? (
                <p>
                  Resend OTP in{" "}
                  <span className="font-semibold text-purple-600">
                    {formatTime(timer)}
                  </span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {isResending ? "Resending..." : "Resend OTP"}
                </button>
              )}
            </div>
          </form>

          {/* Contact Info */}
          <div className="mt-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 text-sm md:text-[16px] pt-2">
            <div className="flex items-center gap-2">
              <MdMailOutline size={20} />
              <p>info@myresearchroom.com</p>
            </div>
            <div className="flex items-center gap-2">
              <IoCallOutline size={20} />
              <p>+91 8767897950</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative md:absolute bottom-1 right-0 left-0 px-4 w-full text-gray-700 text-xs md:text-sm flex flex-col md:flex-row items-end justify-between gap-4 mt-10 md:mt-auto">
        {/* Left */}
        <div className="flex flex-col pl-0 md:pl-8 gap-2 md:gap-4">
          <div className="space-y-1">
            <img src={welogo} alt="logo" className="h-16 w-auto object-cover" />
            <div className="flex gap-3 text-blue-600 underline">
              <a
                href="https://www.linkedin.com/company/wesolutize-technologies/posts/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              <a
                href="https://www.youtube.com/@WeSolutizeTechnologies"
                target="_blank"
                rel="noopener noreferrer"
              >
                YouTube
              </a>
            </div>
          </div>

          <p className="text-gray-600 text-xs md:text-sm text-center md:text-left">
            Designed and Developed by{" "}
            <a
              href="https://www.wesolutize.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline decoration-purple-700 decoration-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-blue-800"
            >
              NeoWesolutize Technologies
            </a>
          </p>
        </div>

        {/* Right */}
        <p className="italic underline cursor-pointer text-right">
          Access is limited to verified users in accordance with{" "}
          <span
            className="hover:text-blue-700"
            onClick={() => setDisclaimerOpen(true)}
          >
            Company Policy.
          </span>
        </p>
      </div>

      <DisclaimerModal
        isOpen={isDisclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
      />
    </div>
  );
}
